import React, { useState, useEffect } from 'react';
import { Typography, makeStyles } from '@material-ui/core';
import Fields from 'components/Fields';
import { Button, Modal } from 'components/Base';
import BulkImport from 'components/BulkImport/BulkImport';
import ManualFund from 'components/Forms/ExistingPolicies/ManagePolicies/Funds/ManualFund';
import { Notify, unFormatPercent } from 'utils';
import api from 'api';
import { connect } from 'react-redux';
import { compose } from 'redux-starter-kit';
import Store from 'store/store';
import SearchModelPortfolio from 'components/ModelPortfolio/Search';

const { selectors } = Store;

const useStyles = makeStyles({
  row: {
    marginBottom: '0.5rem',
    marginTop: '0.5rem',
    flexGrow: 1
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: '0.5rem'
  }
});

const ModelPortfolioAllocationOptions = ({
  addInvestment,
  removeInvestment,
  // eslint-disable-next-line
  setIsModalHidden = () => {},
  cipOptions,
  canAddFundManually = true,
  inline = false,
  canAddCustomPortfolios = false,
  cashFund,
  funds,
  hideCashFund = true
}: {
  addInvestment: Function;
  removeInvestment?: Function;
  setIsModalHidden?: Function;
  cipOptions: Array<any>;
  canAddFundManually?: boolean;
  inline?: boolean;
  canAddCustomPortfolios?: boolean;
  cashFund: any;
  funds?: any;
  hideCashFund?: boolean;
}) => {
  const classes = useStyles();
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [isManualFundModalOpen, setIsManualFundModalOpen] = useState(false);

  useEffect(() => {
    setIsModalHidden(isBulkImportOpen || isManualFundModalOpen);
  }, [isBulkImportOpen, isManualFundModalOpen]);

  const addNewManualFund = (fund: any) => {
    const preparedFund = {
      ...fund,
      ocf: (Number(unFormatPercent(fund.ocf || 0)) / 100).toFixed(4),
      trading_costs: Number(unFormatPercent(fund.trading_costs) / 100).toFixed(
        4
      ),
      asset_classes:
        fund.asset_classes &&
        fund.asset_classes.map((assetClass: any) => ({
          asset_class: assetClass.asset_class,
          percentage_value: (
            Number(unFormatPercent(assetClass.percentage_value)) / 100
          ).toFixed(4)
        }))
    };

    return api
      .addNewFund({ fundData: preparedFund })
      .then(({ data }: any) => {
        addInvestment([data]);
        setIsManualFundModalOpen(false);
      })
      .catch((err: any) => {
        if (err.response.data.message) {
          Notify.error(err.response.data.message);
        }
      });
  };

  const hasCashFund =
    !!funds &&
    funds.some((addedFund: any) => addedFund.fund?.id === cashFund.id);

  const toggleCashFund = (fund: any) => {
    if (hasCashFund && removeInvestment) {
      return removeInvestment(
        funds.findIndex(
          (field: any) => field.fund && field.fund.isin === 'CASH'
        )
      );
    }

    return addInvestment([fund]);
  };

  return (
    <>
      <div className={classes.row}>
        <Typography className={classes.title} variant="h3">
          Funds
        </Typography>
        <div style={{ flexGrow: 1 }}>
          <Fields.Search onSelect={(e: any) => addInvestment([e])} />
        </div>
      </div>
      <div
        style={{ marginTop: '5px', marginBottom: inline ? '0.5rem' : '1rem' }}
      >
        <Button onClick={() => setIsBulkImportOpen(true)}>Bulk Import</Button>
        {canAddFundManually && (
          <Button
            style={{ marginLeft: '1rem' }}
            onClick={() => setIsManualFundModalOpen(true)}
          >
            Add Manually
          </Button>
        )}
      </div>

      <div className={classes.row}>
        <Typography className={classes.title} variant="h3">
          CIP Selection
        </Typography>
        <div style={{ flexGrow: 1 }}>
          <SearchModelPortfolio
            data={
              canAddCustomPortfolios
                ? cipOptions
                : cipOptions.filter((option: any) => !option.custom)
            }
            onSelect={option =>
              addInvestment(
                [{ ...option, id: Number(option.value) }],
                option.type
              )
            }
            isLoading={false}
            inputId="search-cip-mp"
          />
        </div>
      </div>

      <div className={classes.row}>
        <Typography className={classes.title} variant="h3">
          Model Portfolios
        </Typography>
        <div style={{ flexGrow: 1 }}>
          <Fields.ModelPortfolioSearch
            onSelect={(e: any) =>
              addInvestment(
                [
                  {
                    ...e,
                    id: Number(e.value),
                    model_portfolio_selection: true
                  }
                ],
                'mp'
              )
            }
          />
        </div>
      </div>

      <div style={{ marginTop: '1rem' }}>
        {!hideCashFund && (
          <Fields.CheckboxCustom
            isChecked={hasCashFund}
            label="Pound sterling"
            onChange={() => toggleCashFund(cashFund)}
          />
        )}
      </div>

      <BulkImport
        openBulkImport={isBulkImportOpen}
        importToPolicy={addInvestment}
        setOpenBulkImport={setIsBulkImportOpen}
      />

      <Modal
        closeModalFunc={() => setIsManualFundModalOpen(false)}
        isOpen={isManualFundModalOpen}
        disableBackdropClick
        disableEscapeKeyDown
      >
        <ManualFund
          onSubmit={addNewManualFund}
          closeModalFunc={() => setIsManualFundModalOpen(false)}
        />
      </Modal>
    </>
  );
};

export default compose(
  connect(
    (state: any) => ({
      cipOptions: selectors.investmentAnalyserSelectors.cipOptions(state),
      cashFund: selectors.metaSelectors.cashFund(state)
    }),
    null
  )
)(ModelPortfolioAllocationOptions);
